import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {kv} from 'BenchlingUtil';
import {STRING_RESOLUTION_INITIAL_STATE} from 'notebook/tables/string-resolution/reduceStringResolution';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import excess from 'notebook/tables/excess';
import {MODELS_INITIAL_STATE} from 'redux-stores/reduceModels';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';
import {WORKFLOWS_INITIAL_STATE} from 'workflows/reduceWorkflows';

import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';

const SpreadsheetEvalMixin = {
  // Mixin maintains a map of evaluated tables.
  // Must implement:
  //   makeFullTableMap(props) - gives the mapping from table name to full table object (notePart.table)
  //   spreadsheetRoot(props) - gives the container of the tables
  //   selectorsFromProps(props) - gives the selectors array
  //
  // To disable formulas, a component may implement `shouldDisableFormulas()` that returns false.
  //
  // To perform table operations, component should implement (as specified in mediocre/editor):
  //   getCid, getContent, getItems, getRoot, getType, itemsAccessor, setContent,
  //   getSpreadsheetTypes, updateRoot
  //
  // If a component's re-evaluation relies on changes to the reduxStore, it should implement
  // getAllStoreDependenciesFromSpreadsheetRoot returning a mapping from bioentityIds
  // to a list of api identifiers.
  //
  // State:
  //   tableNamespace: a unique namespace to differentiate this component in SpreadsheetStore interactions
  //   fullTableMap: a mapping of table name to full table object present in the prop's steps.
  //   tempFullTableMap: the same mapping as fullTableMap, but it allows changes to be made to it.
  //     By calling tempTableStepUpdateHandler, tempFullTableMap is updated.
  //     By calling tempTableStepResetHandler, the specified table in tempFullTableMap is reset to
  //       the corresponding table present in fullTableMap.
  //       Note: when this component receives updated tables through props, tempFullTableMap will
  //             auto-reset to fullTableMap
  //   computedTables: a mapping of table names to evaluated tables from the tempFullTableMap.
  //     A component can access the evaluated cell from computedTables[tableName][i][j].value
  //     A component can determine if a table has been temporarily modified by comparing:
  //       tables[tableName] == computedTables[tableName]
  //   cellColors: a mapping from [table, row, column] to css classes for cells ranges/refs of
  //     the current formula.
  getInitialState() {
    const tableNamespace = _.uniqueId('table-ns');
    // eslint-disable-next-line no-restricted-syntax
    return _.extend({}, this._spreadsheetStoreState(tableNamespace), {
      tableNamespace,
    });
  },

  UNSAFE_componentWillMount() {
    const fullTableMap = this.makeFullTableMap(this.props);
    const selectors = this.selectorsFromProps(this.props);
    this.setState({tempFullTableMap: fullTableMap});
    this._updateEvaluatedTableState(fullTableMap, selectors);
    this.setState({fullTableMap});
  },

  componentDidMount() {
    this._storeDependencies = this.getAllStoreDependencies(this.props);
    this._storeState = {
      elnResolution: STRING_RESOLUTION_INITIAL_STATE.resolutionResultsByResolutionQuery,
      workflows: WORKFLOWS_INITIAL_STATE,
      fileSummariesById: MODELS_INITIAL_STATE.fileSummariesById,
      storablesById: {},
      storableSummariesById: {},
    };
    // Force an update whenever anything in the ELN redux tree updates (resolution results or
    // resolution loading state), when the stage runs for the entry are changed,
    // or when the bioentities with ids in this._storeDependencies update in the redux store.
    this._reduxUnsubscribe = getStore().subscribe(this._updateIfStoreDependenciesChanged);
    // Listen to StorableStore so that when the containers column of the transfer table fetches storables,
    // the table is re-rendered.
    StorableStore.addChangeListener(this._recomputeTables);
    SpreadsheetStore.addChangeListener(this._spreadsheetStoreChangeHandler);
  },

  componentWillUnmount() {
    if (this._reduxUnsubscribe) {
      this._reduxUnsubscribe();
    }
    StorableStore.removeChangeListener(this._recomputeTables);
    SpreadsheetStore.removeChangeListener(this._spreadsheetStoreChangeHandler);
  },

  UNSAFE_componentWillReceiveProps(nextProps) {
    // If the protocols are the same, no update.
    if (
      this.spreadsheetRoot(this.props) === this.spreadsheetRoot(nextProps) &&
      this.selectorsFromProps(this.props) === this.selectorsFromProps(nextProps)
    ) {
      return;
    }

    // If the tableMaps are the same, no update.
    const fullTableMap = this.makeFullTableMap(nextProps);
    const selectors = this.selectorsFromProps(nextProps);
    if (isEqual(this.state.fullTableMap, fullTableMap) && selectors === this.selectorsFromProps(this.props)) {
      return;
    }
    // Update store dependencies
    this._storeDependencies = this.getAllStoreDependencies(nextProps);

    // Otherwise, update.
    this.setState({tempFullTableMap: fullTableMap});
    this._updateEvaluatedTableState(fullTableMap, selectors);
    this.setState({fullTableMap});
  },

  _handleChangeTempFullTableMap(newTempFullTableMap) {
    this.setState({tempFullTableMap: newTempFullTableMap});
  },

  _updateIfStoreDependenciesChanged() {
    this._storeState = SpreadsheetEvalUtil.updateIfStoreDependenciesChanged({
      storeState: this._storeState,
      storeDependencies: this._storeDependencies,
      tempFullTableMap: this.state.tempFullTableMap,
      handleChangeTempFullTableMap: this._handleChangeTempFullTableMap,
      makeFullTableMap: () => this.makeFullTableMap(this.props),
      selectorsFromProps: () => this.selectorsFromProps(this.props),
      updateEvaluatedTableState: this._updateEvaluatedTableState,
    });
  },

  _recomputeTables() {
    this._updateEvaluatedTableState(this.state.tempFullTableMap, this.selectorsFromProps(this.props));
  },

  _hasFormulasDisabled() {
    return this.shouldDisableFormulas == null ? false : this.shouldDisableFormulas();
  },

  evaluateTables(fullTableMap, selectors) {
    return SpreadsheetEvalUtil.evaluateTables({
      fullTableMap,
      selectors,
      oldComputedTables: this.state.computedTables,
      hasFormulasDisabled: this._hasFormulasDisabled(),
      spreadsheetEvalCacheKey: this.getSpreadsheetEvalCacheKey ? this.getSpreadsheetEvalCacheKey() : null,
    });
  },

  _updateEvaluatedTableState(fullTableMap, selectors) {
    const {computedTables, selectorCounts} = this.evaluateTables(fullTableMap, selectors);
    this.setState({computedTables, selectorCounts});
  },

  _spreadsheetStoreState(tableNamespace) {
    return SpreadsheetEvalUtil.getSpreadsheetStoreState(tableNamespace, this._hasFormulasDisabled());
  },

  _spreadsheetStoreChangeHandler() {
    this.setState(this._spreadsheetStoreState(this.state.tableNamespace));
  },

  setMultiEditingForTable(tableName, {editing, text, typedValue, selection}) {
    SpreadsheetEvalUtil.setMultiEditingForTable(tableName, this.state.tableNamespace, {
      editing,
      text,
      typedValue,
      selection,
    });
  },

  // Wrap @state.editingTable in a method to make it clear that should not be accessed inside a render method,
  // but instead can be accessed while handling an event.
  lookUpTableNameWithActiveEdit() {
    return this.state.editingTable;
  },

  tryInsert(tableName, e) {
    return SpreadsheetEvalUtil.tryInsert({
      tableName,
      e,
      hasFormulasDisabled: this._hasFormulasDisabled(),
      tableNamespace: this.state.tableNamespace,
      editingTable: this.state.editingTable,
    });
  },

  canInsert(e) {
    return SpreadsheetEvalUtil.canInsert({
      e,
      hasFormulasDisabled: this._hasFormulasDisabled(),
      tableNamespace: this.state.tableNamespace,
      editingTable: this.state.editingTable,
    });
  },

  /**
   * Performs an operation on a table, updating the appropriate react components
   * @param  {Number} index   The index of the targetItem containing the table with targetTableName
   * @param  {Function} op    (originalTableMap, targetTableName) -> {tables: newTableMap, box}, where
   *                            originalTableMap {Object}  Maps original names of tables to original tables.
   *                            targetTableName {String}   The name of the table being directly modified
   *                            newTableMap {Object}       Maps *original* names of tables to new tables.
   *                            box {Object}               {minI, maxI, minJ, maxJ}, or null if no selection
   *                                                       is specified.
   * @return null
   */
  _performTableOp(index, op, options) {
    SpreadsheetEvalUtil.performTableOp({
      index,
      op,
      options,
      spreadsheetTypes: this.getSpreadsheetTypes(),
      computedTables: this.state.computedTables,
      getItems: this.getItems,
      getRoot: this.getRoot,
      getType: this.getType,
      getCid: this.getCid,
      getContent: this.getContent,
      setContent: this.setContent,
      requestSelection: this.props.requestSelection,
      updateRoot: this.updateRoot,
      itemsAccessor: this.itemsAccessor,
    });
  },

  // We disable expanding a multiselect via drag while the a table is actively being edited, as this triggers
  // the child table to clear its selection (via multiMouseLeaveHandler), however, the table is still
  // identified by state.editingTable. We let the table keep its selection so that it can handle finalizing
  // the edit.
  disallowMultiExpand() {
    return this.state.editingTable != null;
  },

  deleteTableRows(index, start, end) {
    if (end == null) {
      end = start;
    }
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.deleteRows(tableMap, targetTableName, start, end),
      {forRowRemoval: true, remakeSingleInputs: true}
    );
  },

  deleteTableColumns(index, start, end) {
    if (end == null) {
      end = start;
    }
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.deleteColumns(tableMap, targetTableName, start, end),
      {forColumnEdit: true}
    );
  },

  insertTableRowsAbove(index, start, end) {
    if (end == null) {
      end = start;
    }
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.insertRowsAbove(tableMap, targetTableName, start, end),
      {remakeSingleInputs: true}
    );
  },

  insertTableRowsBelow(index, start, end) {
    if (end == null) {
      end = start;
    }
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.insertRowsBelow(tableMap, targetTableName, start, end),
      {remakeSingleInputs: true}
    );
  },

  reorderTableRowsOrColumns(index, options) {
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.reorderRowsOrColumns(tableMap, targetTableName, options),
      {remakeSingleInputs: true}
    );
  },

  insertTableColumnsLeft(index, start, end, columnInfo = {}) {
    if (end == null) {
      end = start;
    }
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.insertColumnsLeft(tableMap, targetTableName, start, end, columnInfo),
      {forColumnEdit: true, remakeSingleInputs: true}
    );
  },

  insertTableColumnsRight(index, start, end, columnInfo = {}) {
    if (end == null) {
      end = start;
    }
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.insertColumnsRight(tableMap, targetTableName, start, end, columnInfo),
      {forColumnEdit: true, remakeSingleInputs: true}
    );
  },

  renameTable(index, newTableName) {
    this._performTableOp(index, (tableMap, computedTable, targetTableName) =>
      excess.ops.renameTable(tableMap, targetTableName, newTableName)
    );
  },

  mergeTableRegion(index, selectedBox) {
    this._performTableOp(index, (tableMap, computedTables, targetTableName) =>
      excess.ops.mergeRegion(tableMap, computedTables, targetTableName, selectedBox)
    );
  },

  unmergeTableRegion(index, selectedBox) {
    this._performTableOp(index, (tableMap, computedTables, targetTableName) =>
      excess.ops.unmergeRegion(tableMap, computedTables, targetTableName, selectedBox)
    );
  },

  mergeIntoSingleInput(index, startIndex, endIndex) {
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.mergeIntoSingleInput(tableMap, computedTables, targetTableName, startIndex, endIndex),
      {remakeSingleInputs: false}
    );
  },

  unmergeSingleInput(index, startIndex, endIndex) {
    this._performTableOp(
      index,
      (tableMap, computedTables, targetTableName) =>
        excess.ops.unmergeSingleInput(tableMap, computedTables, targetTableName, startIndex, endIndex),
      {remakeSingleInputs: false}
    );
  },

  /**
   * Determines whether tableName is used anywhere within the existing note.
   *
   * @param  {String} tableName    name we are checking for
   * @return {Boolean}             true if tableName has been used in existing note and false otherwise.
   */
  isTableNameUsed(tableName) {
    return this.usedTableNames().includes(tableName);
  },

  getAllStoreDependencies(props) {
    // Not all users of this mixin define `getAllStoreDependenciesFromSpreadsheetRoot`.
    if (!this.getAllStoreDependenciesFromSpreadsheetRoot) {
      return {};
    }
    return this.getAllStoreDependenciesFromSpreadsheetRoot(this.spreadsheetRoot(props));
  },

  updateSelectors(newSelectors) {
    const root = reactUpdate(this.getRoot(), {$merge: {selectors: newSelectors}});
    this.updateRoot(root);
  },

  // Utility methods

  usedTableNames() {
    // eslint-disable-next-line no-restricted-syntax
    return _.keys(this.state.fullTableMap).concat(
      typeof this.extraUsedTableNames === 'function' ? this.extraUsedTableNames() : undefined
    );
  },

  tempTableStepUpdateHandler(fullTable) {
    const {table} = this.state.tempFullTableMap[fullTable.name];
    if (fullTable.table !== table) {
      this._setTableOverride(fullTable.name, fullTable.table);
    }
  },

  tempTableStepResetHandler(name) {
    const newTable = this.state.fullTableMap[name].table;
    this._setTableOverride(name, newTable);
  },

  _setTableOverride(tableName, table) {
    const tempFullTableMap = reactUpdate(this.state.tempFullTableMap, kv(tableName, {$merge: {table}}));
    const selectors = this.selectorsFromProps(this.props);
    this.setState({tempFullTableMap});
    this._updateEvaluatedTableState(tempFullTableMap, selectors);
  },

  getDataForTableItemProps() {
    return {
      tableNamespace: this.state.tableNamespace,
      showExcessLabels: this.state.showExcessLabels,
      computedTables: this.state.computedTables,
      cellColors: this.state.cellColors,
      editingTable: this.state.editingTable,
      selectorCounts: this.state.selectorCounts,
      fullTableMap: this.state.fullTableMap,
    };
  },
};

export default SpreadsheetEvalMixin;
